export type WeekStart = 'monday' | 'sunday'

export interface CalendarDay {
  year: number
  month: number
  day: number
  isWeekend: boolean
}

export interface CalendarWeek {
  isoWeek: number
  days: Array<CalendarDay | null>
}

export interface CalendarMonth {
  year: number
  month: number
  weeks: CalendarWeek[]
}

export interface CalendarOptions {
  weekStart: WeekStart
  showIsoWeeks: boolean
}

const MS_PER_DAY = 86_400_000

export function hasCalendarMonthViewIntent(search: string): boolean {
  return new URLSearchParams(search).get('view') === 'month'
}

export function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

export function isoWeekNumber(date: Date): number {
  const normalized = utcDate(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  )
  const weekday = normalized.getUTCDay() || 7
  normalized.setUTCDate(normalized.getUTCDate() + 4 - weekday)
  const firstDay = utcDate(normalized.getUTCFullYear(), 0, 1)

  return Math.ceil(((normalized.getTime() - firstDay.getTime()) / MS_PER_DAY + 1) / 7)
}

export function createCalendarMonth(
  year: number,
  month: number,
  weekStart: WeekStart,
): CalendarMonth {
  if (!Number.isInteger(year) || year < 1 || year > 9999) {
    throw new RangeError('Calendar year must be an integer between 1 and 9999')
  }
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError('Calendar month must be an integer between 1 and 12')
  }

  const first = utcDate(year, month - 1, 1)
  const startDay = weekStart === 'monday' ? 1 : 0
  const leadingDays = (first.getUTCDay() - startDay + 7) % 7
  const totalDays = daysInMonth(year, month)
  const rowCount = Math.ceil((leadingDays + totalDays) / 7)
  const weeks: CalendarWeek[] = []

  for (let row = 0; row < rowCount; row += 1) {
    const rowStart = utcDate(year, month - 1, 1 - leadingDays + row * 7)
    const isoMonday = new Date(rowStart)
    if (weekStart === 'sunday') isoMonday.setUTCDate(isoMonday.getUTCDate() + 1)

    const days = Array.from({ length: 7 }, (_, column): CalendarDay | null => {
      const day = row * 7 + column - leadingDays + 1
      if (day < 1 || day > totalDays) return null

      const weekday = utcDate(year, month - 1, day).getUTCDay()
      return {
        year,
        month,
        day,
        isWeekend: weekday === 0 || weekday === 6,
      }
    })

    weeks.push({ isoWeek: isoWeekNumber(isoMonday), days })
  }

  return { year, month, weeks }
}

export function createCalendarYear(year: number, weekStart: WeekStart): CalendarMonth[] {
  return Array.from(
    { length: 12 },
    (_, index) => createCalendarMonth(year, index + 1, weekStart),
  )
}

export function enableIsoWeekNumbers(
  enabled: boolean,
  currentWeekStart: WeekStart,
): CalendarOptions {
  return {
    weekStart: enabled ? 'monday' : currentWeekStart,
    showIsoWeeks: enabled,
  }
}

export function selectWeekStart(
  weekStart: WeekStart,
  currentlyShowingIsoWeeks: boolean,
): CalendarOptions {
  return {
    weekStart,
    showIsoWeeks: weekStart === 'sunday' ? false : currentlyShowingIsoWeeks,
  }
}

function utcDate(year: number, monthIndex: number, day: number): Date {
  const date = new Date(0)
  date.setUTCHours(0, 0, 0, 0)
  date.setUTCFullYear(year, monthIndex, day)
  return date
}
